import _ from 'underscore'

import { CallsViewCriteria } from '../trace/callgraph/calls-view'
import {
    categorizeCall,
    computeXhprofNearestFamily,
    XhprofCallEdge,
    XhprofFlatInfoData,
    XhprofNearestFamilyResult,
    XhprofRawData,
    XhprofReport,
} from './XhprofReportGenerator'

export interface XhprofCallsList {
    data: XhprofFlatInfoData
    hasMemory: boolean
}

type ArrayCallback = (input: any[]) => any[]

export const sortXhprofGenericDataObject = (
    data: XhprofFlatInfoData,
    sortBy: string,
    arrayCallback?: ArrayCallback,
): XhprofFlatInfoData => {
    const sortedDataobject = {}
    let sortingArray = Object.keys(data).map(key => ({
        mainKey: key,
        ...data[key],
    }))
    sortingArray.sort((a, b) => {
        const aAbsolute = Math.abs(a[sortBy])
        const bAbsolute = Math.abs(b[sortBy])
        if (aAbsolute < bAbsolute) {
            return 1
        }
        if (aAbsolute > bAbsolute) {
            return -1
        }
        return 0
    })

    if (arrayCallback) {
        sortingArray = arrayCallback(sortingArray)
    }

    sortingArray.forEach(childKey => {
        const childObject = {}
        const parentKey = childKey.mainKey
        Object.keys(childKey).map(childName => {
            const childValue = childKey[childName]
            if (childValue !== parentKey) {
                childObject[childName] = childValue
            }
        })
        sortedDataobject[parentKey] = childObject
    })

    return sortedDataobject
}

export const selectXhprofCalls = (
    data: XhprofFlatInfoData,
    { sortBy, page, filters, query, compareMode }: CallsViewCriteria,
): XhprofCallsList => {
    if (page <= 0) {
        page = 1
    }
    if (['wt', 'ct', 'excl_wt', 'pmu', 'excl_pmu'].indexOf(sortBy) === -1) {
        sortBy = 'excl_wt'
    }
    const overalls = data['main()']
    if (filters.length > 0) {
        data = _.pick(data, (value, call) => {
            const callCategory = categorizeCall(call)
            const matchingFilters = filters.filter(expression => callCategory !== null && callCategory.namespace === expression)

            return matchingFilters.length > 0
        }) as XhprofFlatInfoData
    }

    if (query) {
        data = _.pick(data, (value, key) => key.toLowerCase().indexOf(query.toLowerCase()) !== -1) as XhprofFlatInfoData
    }
    const limit = 50
    data = sortXhprofGenericDataObject(data, sortBy, input => input.slice((page - 1) * limit, page * limit))
    const hasMemory = Boolean(overalls.pmu && overalls.pmu > 0)
    Object.keys(data).map(child => {
        const stats = data[child]
        if (!compareMode) {
            stats.perc_ct = (stats.ct / overalls.ct) * 100
            stats.perc_wt = (stats.wt / overalls.wt) * 100
            stats.perc_excl_wt = (stats.excl_wt / overalls.wt) * 100
            if (hasMemory && stats.pmu && stats.excl_pmu && overalls.pmu) {
                stats.perc_pmu = (stats.pmu / overalls.pmu) * 100
                stats.perc_excl_pmu = (stats.excl_pmu / overalls.pmu) * 100
            } else if (hasMemory) {
                stats.perc_pmu = 0
                stats.perc_excl_pmu = 0
            }
        } else {
            // attempt to explain this different approach
            //
            // when comparing two traces, we need to calculate percentage exclusive wall time differently
            // since it would make for weird comparison values, if calls are in one trace but not the other
            // todo: better explanation
            if (stats.perc_wt && stats.wt && stats.excl_wt && stats.perc_wt > 0 && stats.wt > 0) {
                stats.perc_excl_wt = stats.perc_wt / (stats.wt / stats.excl_wt)
            }
            if (stats.perc_pmu && stats.pmu && stats.excl_pmu && stats.perc_pmu > 0 && stats.pmu > 0) {
                stats.perc_excl_pmu = stats.perc_pmu / (stats.pmu / stats.excl_pmu)
            }
        }
        data[child] = stats
    })
    return { data, hasMemory }
}

export const callXhprofData = (rawData: XhprofRawData, report: XhprofReport, call: string) => {
    return computeXhprofStatsData(rawData, report, call)
}
export class XhprofCall {
    public shortCallName: string
    public namespaceName: string
    public seperator: string

    constructor(callName: string) {
        let argumentCaptureMatchResult = ''

        // PHP 8.4 closure unwrapping
        let closureDepth = 0
        const closureStart = '{closure:'
        let closureTail = ''

        if (callName !== 'main()') {
            callName = callName.replace('()', '')
        }

        while (callName.startsWith(closureStart)) {
            closureDepth++
            closureTail = callName.slice(callName.lastIndexOf(':')) + closureTail
            callName = callName.slice(closureStart.length, callName.lastIndexOf(':'))
        }

        const firstOpeningParenthesis = callName.indexOf('(')
        if (firstOpeningParenthesis >= 0) {
            argumentCaptureMatchResult = callName.slice(firstOpeningParenthesis)
            callName = callName.slice(0, firstOpeningParenthesis)
        }

        const lastBackslash = callName.lastIndexOf('\\')

        if (lastBackslash >= 0) {
            this.seperator = '\\'
            this.namespaceName = callName.slice(0, lastBackslash)
            this.shortCallName = callName.slice(lastBackslash + 1)
        } else if (callName.indexOf('::') !== -1 && callName.indexOf('_') > 0 && callName.indexOf('_') < callName.indexOf('::')) {
            const [classCall, call] = callName.split('::')
            this.seperator = '_'
            this.namespaceName = classCall.slice(0, classCall.lastIndexOf('_'))
            this.shortCallName = classCall.slice(classCall.lastIndexOf('_') + 1) + '::' + call
        } else if (closureDepth > 0 && callName.indexOf('/') >= 0) {
            this.seperator = '/'
            this.namespaceName = callName.slice(0, callName.lastIndexOf('/'))
            this.shortCallName = callName.slice(callName.lastIndexOf('/') + 1)
        } else {
            this.shortCallName = callName
        }
        this.shortCallName = closureStart.repeat(closureDepth) + this.shortCallName + argumentCaptureMatchResult + closureTail
    }
}

export interface XhprofStatsData {
    ct: number
    excl_ct: number
    wt: number
    excl_wt: number
    pmu?: number
    perc_ct?: number
    perc_wt?: number
    perc_excl_wt?: number
    perc_pmu?: number
    perc_excl_pmu?: number
    excl_pmu?: number
    parents: {}
    children: {}
    ct_parents: number
    ct_children: number
    name?: string
    namespace?: string
    hasMemory?: boolean
}

export const computeXhprofStatsData = (
    rawData: XhprofRawData,
    report: XhprofReport,
    call: string,
    diffTrace?: boolean,
): XhprofStatsData => {
    const data: XhprofFlatInfoData = report.data
    const overalls = report.overallTotals

    if (!data[call]) {
        return {
            ct: 0,
            excl_ct: 0,
            wt: 0,
            excl_wt: 0,
            parents: {},
            children: {},
            ct_parents: 0,
            ct_children: 0,
        }
    }
    let hasMemory = false
    if (rawData['main()'].pmu && rawData['main()'].pmu > 0) {
        hasMemory = true
    }

    const callStats = data[call]
    if (!diffTrace) {
        callStats.perc_ct = (callStats.ct / overalls.ct) * 100
        callStats.perc_wt = (callStats.wt / overalls.wt) * 100
        callStats.perc_excl_wt = (callStats.excl_wt / overalls.wt) * 100
        if (callStats.pmu && callStats.excl_pmu && rawData['main()'].pmu && rawData['main()'].pmu > 0) {
            callStats.perc_pmu = (callStats.pmu / rawData['main()'].pmu) * 100
            callStats.perc_excl_pmu = (callStats.excl_pmu / rawData['main()'].pmu) * 100
        }
    } else {
        if (callStats.perc_wt && callStats.wt && callStats.excl_wt && callStats.perc_wt > 0 && callStats.wt > 0) {
            callStats.perc_excl_wt = callStats.perc_wt / (callStats.wt / callStats.excl_wt)
        }
        if (callStats.perc_pmu && callStats.pmu && callStats.excl_pmu && callStats.perc_pmu > 0 && callStats.pmu > 0) {
            callStats.perc_excl_pmu = callStats.perc_pmu / (callStats.pmu / callStats.excl_pmu)
        }
    }

    const result: XhprofNearestFamilyResult = computeXhprofNearestFamily(rawData, call)
    const mergedCallStats = {
        ...callStats,
        ...result,
        name: '',
        namespace: '',
        hasMemory: false,
    }

    Object.keys(mergedCallStats.parents).map(parent => {
        const stats = mergedCallStats.parents[parent]
        stats.perc_wt = mergedCallStats.wt > 0 ? (stats.wt / mergedCallStats.wt) * 100 : 0
        mergedCallStats.parents[parent] = stats
    })

    Object.keys(mergedCallStats.children).map(child => {
        const stats = mergedCallStats.children[child]
        stats.perc_wt = mergedCallStats.wt > 0 ? (stats.wt / mergedCallStats.wt) * 100 : 0
        mergedCallStats.children[child] = stats
    })

    mergedCallStats.parents = sortXhprofGenericDataObject(mergedCallStats.parents, 'wt')
    mergedCallStats.children = sortXhprofGenericDataObject(mergedCallStats.children, 'wt')

    const callName = new XhprofCall(call)
    mergedCallStats.name = callName.shortCallName
    mergedCallStats.namespace = callName.namespaceName
    mergedCallStats.hasMemory = hasMemory

    return mergedCallStats
}

export interface XhprofDiffData {
    call: string
    ct: number
    wt: number
    excl_wt: number
    type: 'changed' | 'removed' | 'added'
    cat: string
}

export const diffXhprofFlatInfoData = (baseData: XhprofFlatInfoData, targetData: XhprofFlatInfoData): XhprofDiffData[] => {
    delete baseData['main()']
    delete targetData['main()']

    const diff = {}
    Object.keys(baseData).map((functionName, index) => {
        const performanceName = baseData[functionName]
        if (!targetData[functionName]) {
            diff[functionName] = {
                call: functionName,
                ct: -performanceName.ct,
                wt: -performanceName.wt,
                excl_wt: -(performanceName.excl_wt ? performanceName.excl_wt : 0),
                type: 'removed',
                cat: performanceName.cat ? performanceName.cat : 'php',
            }
            return
        }
        let wtChange = 0
        let exclWtChange = 0
        let ctChange = 0

        if (baseData[functionName].wt !== targetData[functionName].wt) {
            wtChange = targetData[functionName].wt - baseData[functionName].wt
        }

        if (
            Object.prototype.hasOwnProperty.call(baseData[functionName], 'excl_wt') &&
            Object.prototype.hasOwnProperty.call(targetData[functionName], 'excl_wt')
        ) {
            let targetExclWt: number | undefined = 0
            let baseExclWt: number | undefined = 0
            if (targetData[functionName].excl_wt) {
                targetExclWt = targetData[functionName].excl_wt
            }
            if (baseData[functionName].excl_wt) {
                baseExclWt = baseData[functionName].excl_wt
            }
            if (targetExclWt && baseExclWt) {
                exclWtChange = targetExclWt - baseExclWt
            }
        }

        if (baseData[functionName].ct !== targetData[functionName].ct) {
            ctChange = targetData[functionName].ct - baseData[functionName].ct
        }
        if (wtChange || ctChange || exclWtChange) {
            diff[functionName] = {
                call: functionName,
                ct: ctChange,
                wt: wtChange,
                excl_wt: exclWtChange,
                type: 'changed',
                cat: targetData[functionName].cat ? targetData[functionName].cat : 'php',
            }
        }
        delete targetData[functionName]
    })

    Object.keys(targetData).map((functionName, index) => {
        const performanceName = targetData[functionName]
        diff[functionName] = {
            call: functionName,
            ct: performanceName.ct,
            wt: performanceName.wt,
            excl_wt: performanceName.excl_wt,
            type: 'added',
            cat: targetData[functionName].cat ? targetData[functionName].cat : 'php',
        }
    })
    const diffValues = Object.keys(diff).map(key => ({
        ...diff[key],
    }))

    return diffValues
}

export const stackTraceXhprofParents = (data: XhprofFlatInfoData, call: string): XhprofFlatInfoData => {
    return reverseXhprofStacktrace(data, call, call, {})
}

const reverseXhprofStacktrace = (
    data: XhprofFlatInfoData,
    call: string,
    startCall: string,
    stackTraceParents: XhprofFlatInfoData,
): XhprofFlatInfoData => {
    const total = data['main()'].wt
    const callInfo = data[call]

    // cycle detection
    if (call in stackTraceParents) {
        return stackTraceParents
    }

    if (Object.keys(stackTraceParents).length > 15) {
        return stackTraceParents
    }

    // skip the start call in the list of parents
    if (call !== startCall) {
        stackTraceParents[call] = callInfo
        stackTraceParents[call].perc_wt = (callInfo.wt / total) * 100
        stackTraceParents[call].perc_excl_wt = (callInfo.excl_wt / total) * 100
    }

    if (Object.keys(callInfo.parents).length > 0) {
        const slowestCall = Object.keys(callInfo.parents)
            .map(parentName => callInfo.parents[parentName])
            .sort((callA, callB) => callA.wt - callB.wt)
            .pop() as XhprofCallEdge

        reverseXhprofStacktrace(data, slowestCall.name, startCall, stackTraceParents)
    }

    return stackTraceParents
}
